import * as geojson from 'geojson';
import React from 'react';

import * as mapUtils from 'app/utils/mapUtils';

import {
  GridFeatureCollection,
  cellBbox,
  cellIdToColRow,
  colRowToCellId,
  defaultSpecFromGeometry,
  filterCellsToProperty,
  findNextExistingCell,
  gridSpecToFeatureCollection,
} from './gridGeometry';
import {ArrowDirection, CellId, GridSpec, Size} from './types';

const ZOOM_DURATION_MS = 700;
const ZOOM_PADDING_PX = 60;

const ARROW_DIRECTIONS: Record<string, ArrowDirection> = {
  ArrowRight: 'right',
  ArrowLeft: 'left',
  ArrowDown: 'down',
  ArrowUp: 'up',
};

interface GridGeometry {
  spec: GridSpec | null;
  featureCollection: GridFeatureCollection | null;
  existingCellIds: Set<CellId> | null;
}

/**
 * Builds the GridSpec, expands it to a GeoJSON FeatureCollection, and clips
 * cells to the property polygon. All three values are memoized on
 * (featureGeometry, size) so they update together.
 */
export function useGridGeometry(
  featureGeometry: geojson.Geometry | null,
  size: Size | null
): GridGeometry {
  return React.useMemo(() => {
    if (!featureGeometry || size === null)
      return {spec: null, featureCollection: null, existingCellIds: null};
    const spec = defaultSpecFromGeometry(featureGeometry, size);
    const fullFc = gridSpecToFeatureCollection(spec);
    const fc = filterCellsToProperty(fullFc, featureGeometry);
    const existingCellIds = new Set<CellId>(
      fc.features.map((f) => colRowToCellId(f.properties.col, f.properties.row))
    );
    return {spec, featureCollection: fc, existingCellIds};
  }, [featureGeometry, size]);
}

/**
 * Animate the map camera to the bbox of the active cell.
 *
 * Only the camera leader runs this (gridCameraEnabled = isCameraLeader). In
 * compare mode that's the after-map; the before-map mirrors it via the
 * one-directional sync in CompareMap, so a single animated fitBounds on the
 * leader keeps both maps aligned.
 */
export function useGridCamera(
  map: mapboxgl.Map,
  spec: GridSpec | null,
  activeCellId: CellId | null,
  gridCameraEnabled = true
) {
  React.useEffect(() => {
    if (!gridCameraEnabled || !activeCellId || !spec) return;
    const [west, south, east, north] = cellBbox(spec, activeCellId);
    const bounds: mapboxgl.LngLatBoundsLike = [
      [west, south],
      [east, north],
    ];
    mapUtils.safeFitBounds(map, bounds, {padding: ZOOM_PADDING_PX, duration: ZOOM_DURATION_MS});
  }, [gridCameraEnabled, activeCellId, spec, map]);
}

/**
 * Window-level capture-phase keydown listener for arrow-key navigation between
 * cells. Capture phase fires before Blueprint's bubble-phase global hotkeys,
 * so this overrides existing arrow shortcuts whenever the hook is mounted.
 * Wraps around at edges and skips cells absent from the filtered grid.
 */
export function useGridKeyboardNav(
  spec: GridSpec | null,
  existingCellIds: Set<CellId> | null,
  activeCellId: CellId | null,
  setActiveCellId: React.Dispatch<React.SetStateAction<CellId | null>>,
  toggleCellReviewed: (id: CellId) => void,
  isEnabled: boolean,
  /* Hook only registers a listener when true. PropertyGrid renders once per map,
   * so without this gate compare mode mounts two listeners and R presses fire
   * toggleCellReviewed twice — net-zero change but with a visible flash. */
  isPrimaryMap = true
) {
  // The window keydown listener needs the latest values, but listing them
  // as effect deps would re-attach the listener every time the active cell
  // changes. Stashing them in a ref instead keeps the listener stable. The
  // ref is updated in a layout effect (not in render) because React 18+
  // doesn't allow writing to refs during render.
  const latestRef = React.useRef({
    spec,
    existingCellIds,
    activeCellId,
    setActiveCellId,
    toggleCellReviewed,
  });
  React.useLayoutEffect(() => {
    latestRef.current = {spec, existingCellIds, activeCellId, setActiveCellId, toggleCellReviewed};
  });

  React.useEffect(() => {
    if (!isEnabled || !isPrimaryMap) return;

    const handler = (ev: KeyboardEvent) => {
      const {spec, existingCellIds, activeCellId, setActiveCellId, toggleCellReviewed} =
        latestRef.current;
      if (!spec || !existingCellIds || existingCellIds.size === 0) return;

      const target = ev.target as HTMLElement | null;
      if (target && /^(INPUT|TEXTAREA|SELECT)$/i.test(target.tagName)) return;
      if (target && target.isContentEditable) return;

      if (ev.key === 'r' || ev.key === 'R') {
        if (!activeCellId) return;
        //also early exit if they are tryna keyboard refresh
        if (ev.metaKey || ev.ctrlKey || ev.altKey) return;
        ev.preventDefault();
        ev.stopPropagation();
        toggleCellReviewed(activeCellId);
        return;
      }

      const arrow = ARROW_DIRECTIONS[ev.key];
      if (!arrow) return;

      ev.preventDefault();
      ev.stopPropagation();

      const [startCol, startRow] = activeCellId ? cellIdToColRow(activeCellId) : [0, 0];

      const next = findNextExistingCell(
        startCol,
        startRow,
        arrow,
        spec.cols,
        spec.rows,
        existingCellIds
      );
      if (next) setActiveCellId(next);
    };

    window.addEventListener('keydown', handler, true);
    return () => {
      window.removeEventListener('keydown', handler, true);
    };
  }, [isEnabled, isPrimaryMap]);
}
